import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bell, Mail, Smartphone, X, Loader2, Send } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '../lib/api';
import { t } from '../lib/i18n';
import { useAuth } from '../context/AuthContext';
import { pushSupported, subscribePush, unsubscribePush, currentSubscription } from '../lib/push';

export const openAlerts = () => window.dispatchEvent(new CustomEvent('dealgo:alerts'));

const Toggle = ({ on, onChange, disabled, testId }) => (
  <button type="button" role="switch" aria-checked={on} disabled={disabled} onClick={() => onChange(!on)} data-testid={testId}
    className={`relative w-11 h-6 rounded-full transition-colors shrink-0 disabled:opacity-40 ${on ? 'bg-[#E50914]' : 'bg-[#404040]'}`}>
    <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition-all ${on ? 'start-[22px]' : 'start-0.5'}`} />
  </button>
);

const Row = ({ icon: Icon, title, hint, children, testId }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg bg-black/40 border border-[#262626]" data-testid={testId}>
    <span className="w-9 h-9 rounded-full bg-[#E50914]/15 text-[#E50914] flex items-center justify-center shrink-0"><Icon size={16} /></span>
    <div className="min-w-0 flex-1"><p className="text-sm font-bold text-white">{title}</p><p className="text-xs text-[#808080]">{hint}</p></div>
    {children}
  </div>
);

const AlertsDialog = () => {
  const { user, openAuth } = useAuth();
  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(null);
  const [vapid, setVapid] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onOpen = () => { if (!user) return openAuth(); setOpen(true); };
    window.addEventListener('dealgo:alerts', onOpen);
    return () => window.removeEventListener('dealgo:alerts', onOpen);
  }, [user, openAuth]);

  useEffect(() => {
    if (!open) return;
    api.get('/auth/me/alerts').then((r) => { setPrefs(r.data.prefs); setVapid(r.data.vapid_public_key); }).catch(() => setPrefs({ email: true, push: true }));
    currentSubscription().then((s) => setSubscribed(!!s)).catch(() => setSubscribed(false));
  }, [open]);

  if (!open || !user) return null;
  const save = async (next) => {
    setPrefs(next);
    try { await api.put('/auth/me/alerts', next); } catch (e) { toast.error(formatApiError(e)); }
  };
  const togglePush = async (on) => {
    setBusy(true);
    try {
      if (on) { await subscribePush(vapid); setSubscribed(true); await save({ ...prefs, push: true }); toast.success(t('Notifications push activées sur cet appareil')); }
      else { await unsubscribePush(); setSubscribed(false); await save({ ...prefs, push: false }); }
    } catch (e) {
      toast.error(e.message === 'denied' ? t('Les notifications sont bloquées dans les paramètres de votre navigateur') : e.message === 'unsupported' ? t('Les notifications push ne sont pas prises en charge par ce navigateur') : formatApiError(e));
    } finally { setBusy(false); }
  };
  const test = async () => {
    try { const r = await api.post('/push/test'); toast.success(`${t('Notification de test envoyée')} (${r.data.sent}/${r.data.total})`); } catch (e) { toast.error(formatApiError(e)); }
  };
  const followed = user.followed_brands?.length || 0;
  return (
    <div className="fixed inset-0 z-[140] flex items-center justify-center p-4" data-testid="alerts-dialog">
      <button aria-label={t('Fermer')} className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setOpen(false)} data-testid="alerts-backdrop" />
      <div className="relative w-full max-w-md bg-[#141414] border border-[#262626] rounded-2xl shadow-2xl p-6 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="text-lg font-black text-white flex items-center gap-2"><Bell size={18} className="text-[#E50914]" /> {t('Alertes nouveaux catalogues')}</h2>
            <p className="text-xs text-[#B3B3B3] mt-1">{t('Soyez le premier informé quand un magasin que vous suivez publie une nouvelle parution.')}</p>
          </div>
          <button onClick={() => setOpen(false)} aria-label={t('Fermer')} data-testid="alerts-close" className="w-9 h-9 rounded-full hover:bg-white/10 text-white flex items-center justify-center"><X size={18} /></button>
        </div>
        <p className="text-xs text-[#808080]" data-testid="alerts-followed-count">
          {followed > 0 ? `${t('Vous suivez')} ${followed} ${followed > 1 ? t('magasins') : t('magasin')}. ` : `${t('Vous ne suivez aucun magasin pour le moment.')} `}
          <Link to="/magasins" onClick={() => setOpen(false)} className="text-[#E50914] font-semibold hover:underline" data-testid="alerts-manage-stores">{t('Gérer les magasins que vous suivez')}</Link>
        </p>
        {!prefs ? <div className="flex justify-center py-6"><Loader2 className="animate-spin text-[#E50914]" size={22} /></div> : (
          <div className="space-y-2">
            <Row icon={Mail} title={t('Alerte e-mail')} hint={user.email} testId="alerts-email-row">
              <Toggle on={prefs.email} onChange={(v) => save({ ...prefs, email: v })} testId="alerts-email-toggle" />
            </Row>
            <Row icon={Smartphone} title={t('Notification push web')} hint={!pushSupported() ? t('Les notifications push ne sont pas prises en charge par ce navigateur') : subscribed && prefs.push ? t('Activé sur cet appareil') : t('Notification instantanée sur cet appareil')} testId="alerts-push-row">
              {busy ? <Loader2 className="animate-spin text-[#E50914]" size={18} /> : <Toggle on={subscribed && prefs.push} disabled={!pushSupported()} onChange={togglePush} testId="alerts-push-toggle" />}
            </Row>
            {subscribed && prefs.push && (
              <button onClick={test} data-testid="alerts-push-test" className="inline-flex items-center gap-2 text-xs font-semibold text-[#B3B3B3] hover:text-white"><Send size={12} /> {t('Envoyer une notification de test')}</button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertsDialog;
